import { useEffect } from "react";
import { type Branding } from "./BrandingProvider";

/**
 * Apply branding effects: brand-color CSS custom properties.
 * The tenant favicon is deliberately not applied — the portal keeps the
 * Reflex Labs favicon. This hook should be called within a component that has
 * access to branding data.
 */
export function useBrandingEffects(branding: Branding | null) {
  useEffect(() => {
    if (!branding) return;

    const root = document.documentElement;

    // Apply primary color as CSS custom property. --color-tide is *derived*
    // from --brand-primary in app.css (dark mode lifts it for contrast via
    // color-mix), so only the brand var is set here — setting --color-tide
    // inline would override the dark-mode derivation.
    if (branding.primaryColor) {
      root.style.setProperty("--brand-primary", branding.primaryColor);
    } else {
      // Revert to the default Legal HD color (the CSS fallback) when no branding
      root.style.removeProperty("--brand-primary");
    }

    // Secondary accent — exposed as a CSS var for later use; no default.
    if (branding.secondaryColor) {
      root.style.setProperty("--brand-secondary", branding.secondaryColor);
    } else {
      root.style.removeProperty("--brand-secondary");
    }

    // NOTE: background_color is deliberately NOT applied to the portal chrome.
    // It's the band behind the logo in *customer emails* (and the tenant's own
    // public site) — putting it behind the logo in this admin portal's nav is
    // not wanted. Email rendering applies it server-side via Jinja.

    // NOTE: the tenant's faviconUrl is intentionally NOT applied to the portal.
    // The portal keeps the Reflex Labs favicon (/favicon.svg from index.html);
    // a tenant's favicon is for their own public site, not this admin portal.

    // Cleanup on unmount
    return () => {
      root.style.removeProperty("--brand-primary");
      root.style.removeProperty("--brand-secondary");
    };
  }, [branding]);
}
